import { useTranslation } from 'react-i18next'
import { Languages } from 'lucide-react'
import { changeLanguage } from '@/lib/i18n'
import { LANGUAGES, type UiLanguage } from '@/lib/types'
import { cn } from '@/lib/utils'

/**
 * §11.6 — a segmented control of EN | RU | UZ. The labels are the language
 * codes, which are identical in every language, so the control never resizes
 * when the interface language changes (§13.3).
 */
export function LanguageSwitcher({
  onChange,
  compact = false,
  className,
}: {
  onChange?: (language: UiLanguage) => void
  compact?: boolean
  className?: string
}) {
  const { t, i18n } = useTranslation()
  const active = i18n.resolvedLanguage ?? 'en'

  async function select(language: UiLanguage): Promise<void> {
    await changeLanguage(language)
    onChange?.(language)
  }

  if (compact) {
    return (
      <label className={cn('inline-flex items-center gap-2', className)}>
        <Languages aria-hidden className="size-4 text-text-muted" strokeWidth={1.75} />
        <span className="sr-only">{t('language.switch')}</span>
        <select
          value={active}
          onChange={(e) => void select(e.target.value as UiLanguage)}
          className="rounded-sm border border-border-strong bg-surface px-2 py-1 text-meta"
        >
          {LANGUAGES.map((language) => (
            <option key={language} value={language}>
              {language.toUpperCase()}
            </option>
          ))}
        </select>
      </label>
    )
  }

  return (
    <div
      role="group"
      aria-label={t('language.switch')}
      className={cn('inline-flex rounded-sm border border-border-strong p-[2px]', className)}
    >
      {LANGUAGES.map((language) => (
        <button
          key={language}
          type="button"
          aria-pressed={active === language}
          onClick={() => void select(language)}
          className={cn(
            'rounded-[4px] px-2 py-1 text-label transition-colors',
            active === language
              ? 'bg-itpu-teal text-white'
              : 'text-text-muted hover:bg-surface-tint hover:text-text',
          )}
        >
          <span className="sr-only">{t(`language.${language}`)}</span>
          <span aria-hidden>{language.toUpperCase()}</span>
        </button>
      ))}
    </div>
  )
}
